import { supabase } from '../supabaseClient';

// Helper function to get LED data for an order
export async function getLedDataForOrder(orderNum) {
  try {
    // Try exact match first
    const { data: mainData } = await supabase
      .from('ROEMainInfo')
      .select('info7, info6, info4')
      .eq('info1', orderNum)
      .limit(1);

    const { data: subData } = await supabase
      .from('ROESubInfo')
      .select('info5, info6, info7, info8, info9, info10, info11, info12, info13')
      .eq('info1', orderNum)
      .limit(1);

    // If exact match found, return it
    if (mainData?.length > 0 || subData?.length > 0) {
      return {
        leds: mainData?.[0]?.info7 || null,
        firmware_version: mainData?.[0]?.info6 || null,
        product_family: mainData?.[0]?.info4 || null,
        r_nm: subData?.[0]?.info5 || null,
        r_mcd: subData?.[0]?.info6 || null,
        r_v: subData?.[0]?.info7 || null,
        g_nm: subData?.[0]?.info8 || null,
        g_mcd: subData?.[0]?.info9 || null,
        g_v: subData?.[0]?.info10 || null,
        b_nm: subData?.[0]?.info11 || null,
        b_mcd: subData?.[0]?.info12 || null,
        b_v: subData?.[0]?.info13 || null
      };
    }

    // If no exact match, try to find a related sub-order
    const { data: mainDataLike } = await supabase
      .from('ROEMainInfo')
      .select('info7, info6, info4')
      .like('info1', `${orderNum}%`)
      .limit(1);

    const { data: subDataLike } = await supabase
      .from('ROESubInfo')
      .select('info5, info6, info7, info8, info9, info10, info11, info12, info13')
      .like('info1', `${orderNum}%`)
      .limit(1);

    return {
      leds: mainDataLike?.[0]?.info7 || null,
      firmware_version: mainDataLike?.[0]?.info6 || null,
      product_family: mainDataLike?.[0]?.info4 || null,
      r_nm: subDataLike?.[0]?.info5 || null,
      r_mcd: subDataLike?.[0]?.info6 || null,
      r_v: subDataLike?.[0]?.info7 || null,
      g_nm: subDataLike?.[0]?.info8 || null,
      g_mcd: subDataLike?.[0]?.info9 || null,
      g_v: subDataLike?.[0]?.info10 || null,
      b_nm: subDataLike?.[0]?.info11 || null,
      b_mcd: subDataLike?.[0]?.info12 || null,
      b_v: subDataLike?.[0]?.info13 || null
    };
  } catch (error) {
    console.error('Error getting LED data for order:', orderNum, error);
    return {
      leds: null,
      firmware_version: null,
      product_family: null,
      r_nm: null, r_mcd: null, r_v: null,
      g_nm: null, g_mcd: null, g_v: null,
      b_nm: null, b_mcd: null, b_v: null
    };
  }
}

// Universal search function - tries all search types
export async function searchAll(queryCode, page = 1, pageSize = 20) {
  try {
    // Try order number first
    let results = await searchDatabase('order_num', queryCode, page, pageSize);
    if (results.data && results.data.length > 0) {
      return { ...results, searchType: 'order_num' };
    }

    // Try tile serial number
    results = await searchDatabase('tile_sn', queryCode, page, pageSize);
    if (results.data && results.data.length > 0) {
      return { ...results, searchType: 'tile_sn' };
    }

    // Try module serial number
    results = await searchDatabase('module_sn', queryCode, page, pageSize);
    if (results.data && results.data.length > 0) {
      return { ...results, searchType: 'module_sn' };
    }

    // Nothing found in any dataset
    return { data: [], pagination: null, searchType: null };
  } catch (error) {
    console.error('Universal search error:', error);
    throw error;
  }
}

// Search function - handles tile_sn, module_sn, and order_num searches
export async function searchDatabase(type, queryCode, page = 1, pageSize = 20) {
  const offset = (page - 1) * pageSize;

  try {
    let results = { data: [], pagination: null };

    switch (type) {
      case "tile_sn":
        // For tile_sn, get all results without pagination
        const { data: tileData, error: tileError, count } = await supabase
          .from('ROEList')
          .select('ROEMainBar, ROESubBar, ROEPosition, dodno', { count: 'exact' })
          .eq('ROEMainBar', queryCode);

        if (tileError) throw tileError;

        if (tileData && tileData.length > 0) {
          // Get LED data for unique orders
          const uniqueOrders = [...new Set(tileData.map(item => item.dodno))];
          const ledDataPromises = uniqueOrders.map(orderNum => getLedDataForOrder(orderNum));
          const ledDataResults = await Promise.all(ledDataPromises);

          const ledDataMap = {};
          uniqueOrders.forEach((orderNum, index) => {
            ledDataMap[orderNum] = ledDataResults[index];
          });

          results = {
            data: tileData.map(item => {
              const ledData = ledDataMap[item.dodno] || {};
              return {
                order_num: item.dodno,
                tile_sn: item.ROEMainBar,
                module_sn: item.ROESubBar,
                position: item.ROEPosition,
                leds: ledData.leds || null,
                firmware_version: ledData.firmware_version || null,
                product_family: ledData.product_family || null,
                r_nm: ledData.r_nm || null,
                r_mcd: ledData.r_mcd || null,
                r_v: ledData.r_v || null,
                g_nm: ledData.g_nm || null,
                g_mcd: ledData.g_mcd || null,
                g_v: ledData.g_v || null,
                b_nm: ledData.b_nm || null,
                b_mcd: ledData.b_mcd || null,
                b_v: ledData.b_v || null
              };
            }),
            pagination: {
              page: 1,
              pageSize: count,
              total: count,
              totalPages: 1,
              hasNext: false,
              hasPrev: false
            }
          };
        }
        break;

      case "module_sn":
        const { data: moduleData, error: moduleError, count: moduleCount } = await supabase
          .from('ROEList')
          .select('ROEMainBar, ROESubBar, ROEPosition, dodno', { count: 'exact' })
          .eq('ROESubBar', queryCode)
          .range(offset, offset + pageSize - 1);

        if (moduleError) throw moduleError;

        if (moduleData && moduleData.length > 0) {
          const uniqueOrders = [...new Set(moduleData.map(item => item.dodno))];
          const ledDataPromises = uniqueOrders.map(orderNum => getLedDataForOrder(orderNum));
          const ledDataResults = await Promise.all(ledDataPromises);

          const ledDataMap = {};
          uniqueOrders.forEach((orderNum, index) => {
            ledDataMap[orderNum] = ledDataResults[index];
          });

          results = {
            data: moduleData.map(item => {
              const ledData = ledDataMap[item.dodno] || {};
              return {
                order_num: item.dodno,
                tile_sn: item.ROEMainBar,
                module_sn: item.ROESubBar,
                position: item.ROEPosition,
                leds: ledData.leds || null,
                firmware_version: ledData.firmware_version || null,
                product_family: ledData.product_family || null,
                r_nm: ledData.r_nm || null,
                r_mcd: ledData.r_mcd || null,
                r_v: ledData.r_v || null,
                g_nm: ledData.g_nm || null,
                g_mcd: ledData.g_mcd || null,
                g_v: ledData.g_v || null,
                b_nm: ledData.b_nm || null,
                b_mcd: ledData.b_mcd || null,
                b_v: ledData.b_v || null
              };
            }),
            pagination: {
              page,
              pageSize,
              total: moduleCount,
              totalPages: Math.ceil(moduleCount / pageSize),
              hasNext: page < Math.ceil(moduleCount / pageSize),
              hasPrev: page > 1
            }
          };
        }
        break;

      case "order_num":
        const { data: orderData, error: orderError, count: orderCount } = await supabase
          .from('ROEList')
          .select('ROEMainBar, ROESubBar, ROEPosition, dodno', { count: 'exact' })
          .eq('dodno', queryCode)
          .range(offset, offset + pageSize - 1);

        if (orderError) throw orderError;

        if (orderData && orderData.length > 0) {
          const ledData = await getLedDataForOrder(queryCode);

          results = {
            data: orderData.map(item => ({
              order_num: item.dodno,
              tile_sn: item.ROEMainBar,
              module_sn: item.ROESubBar,
              position: item.ROEPosition,
              leds: ledData.leds || null,
              firmware_version: ledData.firmware_version || null,
              product_family: ledData.product_family || null,
              r_nm: ledData.r_nm || null,
              r_mcd: ledData.r_mcd || null,
              r_v: ledData.r_v || null,
              g_nm: ledData.g_nm || null,
              g_mcd: ledData.g_mcd || null,
              g_v: ledData.g_v || null,
              b_nm: ledData.b_nm || null,
              b_mcd: ledData.b_mcd || null,
              b_v: ledData.b_v || null
            })),
            pagination: {
              page,
              pageSize,
              total: orderCount,
              totalPages: Math.ceil(orderCount / pageSize),
              hasNext: page < Math.ceil(orderCount / pageSize),
              hasPrev: page > 1
            }
          };
        }
        break;

      default:
        throw new Error("Invalid type provided");
    }

    return results;
  } catch (error) {
    console.error('Search error:', error);
    throw error;
  }
}

// Get product info for a search query
export async function getProductInfo(type, queryCode) {
  try {
    let productInfo = {};

    // Helper function to get distinct count
    const getDistinctCount = async (table, column, whereColumn, whereValue) => {
      const uniqueValues = new Set();
      let offset = 0;
      const chunkSize = 1000;

      while (true) {
        const { data, error } = await supabase
          .from(table)
          .select(column)
          .eq(whereColumn, whereValue)
          .range(offset, offset + chunkSize - 1);

        if (error) throw error;
        if (!data || data.length === 0) break;

        data.forEach(row => uniqueValues.add(row[column]));
        if (data.length < chunkSize) break;
        offset += chunkSize;
      }

      return uniqueValues.size;
    };

    // Helper to get order info from MainInfo
    const getOrderInfoFromMainInfo = async (orderNum) => {
      const { data: mainData } = await supabase
        .from('ROEMainInfo')
        .select('info6, info4')
        .eq('info1', orderNum)
        .limit(1);

      if (mainData && mainData.length > 0) {
        return {
          firmware_version: mainData[0].info6 || null,
          product_family: mainData[0].info4 || null
        };
      }

      const { data: mainDataLike } = await supabase
        .from('ROEMainInfo')
        .select('info6, info4')
        .like('info1', `${orderNum}%`)
        .limit(1);

      return {
        firmware_version: mainDataLike?.[0]?.info6 || null,
        product_family: mainDataLike?.[0]?.info4 || null
      };
    };

    switch (type) {
      case "tile_sn":
      case "module_sn":
        const column = type === "tile_sn" ? "ROEMainBar" : "ROESubBar";
        const { data: rows, error } = await supabase
          .from('ROEList')
          .select('dodno, ROEMainBar, ROESubBar, TileType, DriverIC, Control_System')
          .eq(column, queryCode);

        if (error) throw error;

        if (rows && rows.length > 0) {
          const uniqueOrders = [...new Set(rows.map(row => row.dodno))];

          let totalModules = 0;
          let totalTiles = 0;
          const allTileTypes = [];
          const allDriverICs = [];
          const allControlSystems = [];

          for (const orderNum of uniqueOrders) {
            const moduleCount = await getDistinctCount('ROEList', 'ROESubBar', 'dodno', orderNum);
            const tileCount = await getDistinctCount('ROEList', 'ROEMainBar', 'dodno', orderNum);

            totalModules += moduleCount;
            totalTiles += tileCount;

            const { data: orderData } = await supabase
              .from('ROEList')
              .select('TileType, DriverIC, Control_System')
              .eq('dodno', orderNum)
              .limit(100);

            if (orderData && orderData.length > 0) {
              orderData.forEach(row => {
                if (row.TileType && row.TileType !== "NULL") allTileTypes.push(row.TileType);
                if (row.DriverIC && row.DriverIC !== "NULL") allDriverICs.push(row.DriverIC);
                if (row.Control_System && row.Control_System !== "NULL") allControlSystems.push(row.Control_System);
              });
            }
          }

          const uniqueTileTypes = [...new Set(allTileTypes)];
          const uniqueDriverICs = [...new Set(allDriverICs)];
          const uniqueControlSystems = [...new Set(allControlSystems)];

          const firstOrderInfo = await getOrderInfoFromMainInfo(uniqueOrders[0]);

          productInfo = {
            searchType: type,
            searchValue: queryCode,
            totalModules: totalModules,
            totalTiles: totalTiles,
            totalOrders: uniqueOrders.length,
            orders: uniqueOrders,
            tileTypes: uniqueTileTypes,
            driverICs: uniqueDriverICs,
            controlSystems: uniqueControlSystems,
            firmware_version: firstOrderInfo.firmware_version,
            product_family: firstOrderInfo.product_family
          };
        }
        break;

      case "order_num":
        const { data: orderRows, error: orderError } = await supabase
          .from('ROEList')
          .select('dodno, ROEMainBar, ROESubBar, TileType, DriverIC, Control_System')
          .eq('dodno', queryCode);

        if (orderError) throw orderError;

        if (orderRows && orderRows.length > 0) {
          const moduleCount = await getDistinctCount('ROEList', 'ROESubBar', 'dodno', queryCode);
          const tileCount = await getDistinctCount('ROEList', 'ROEMainBar', 'dodno', queryCode);
          const orderInfo = await getOrderInfoFromMainInfo(queryCode);

          const uniqueTileTypes = [...new Set(orderRows.map(row => row.TileType).filter(val => val && val !== "NULL"))];
          const uniqueDriverICs = [...new Set(orderRows.map(row => row.DriverIC).filter(val => val && val !== "NULL"))];
          const uniqueControlSystems = [...new Set(orderRows.map(row => row.Control_System).filter(val => val && val !== "NULL"))];

          productInfo = {
            searchType: "order_num",
            searchValue: queryCode,
            totalModules: moduleCount,
            totalTiles: tileCount,
            totalOrders: 1,
            orders: [queryCode],
            tileTypes: uniqueTileTypes,
            driverICs: uniqueDriverICs,
            controlSystems: uniqueControlSystems,
            firmware_version: orderInfo.firmware_version,
            product_family: orderInfo.product_family
          };
        }
        break;
      default:
        break;
    }

    return productInfo;
  } catch (error) {
    console.error('Product info error:', error);
    throw error;
  }
}

// Get all positions for an order
export async function getOrderPositions(orderNum) {
  try {
    const { data: positions, error } = await supabase
      .from('ROEList')
      .select('ROEPosition')
      .eq('dodno', orderNum);

    if (error) throw error;

    const uniquePositions = [...new Set(positions.map(p => p.ROEPosition))]
      .filter(pos => pos)
      .map(pos => ({ position: pos }));

    return uniquePositions;
  } catch (error) {
    console.error('Error fetching positions:', error);
    return [];
  }
}

// Get orders with matching LED batch
export async function getOrdersByLedBatch(leds) {
  try {
    const { data: ordersWithSameLeds, error } = await supabase
      .from('ROEMainInfo')
      .select('info1')
      .eq('info7', leds);

    if (error) throw error;

    if (!ordersWithSameLeds || ordersWithSameLeds.length === 0) {
      return [];
    }

    // Filter out sub-orders (those with hyphens and suffixes)
    const mainOrders = ordersWithSameLeds.filter(order => {
      const orderNum = order.info1;
      const hasSuffix = /-[SMsm]?\d*$/.test(orderNum);
      return !hasSuffix;
    });

    return mainOrders;
  } catch (error) {
    console.error('Error fetching orders by LEDs:', error);
    return [];
  }
}

// Batch panel serial to order number lookup
export async function batchPanelSearch(panelSerials) {
  try {
    const validSerials = panelSerials
      .filter(serial => serial && typeof serial === 'string' && serial.trim().length > 0)
      .map(serial => serial.trim());

    if (validSerials.length === 0) {
      return {
        summary: {
          total_requested: panelSerials.length,
          valid_serials: 0,
          found_matches: 0,
          not_found: 0
        },
        results: []
      };
    }

    // Query in chunks to avoid hitting Supabase limits
    const chunkSize = 100;
    let allResults = [];

    for (let i = 0; i < validSerials.length; i += chunkSize) {
      const chunk = validSerials.slice(i, i + chunkSize);

      const { data: chunkResults, error } = await supabase
        .from('ROEList')
        .select('ROEMainBar, dodno')
        .in('ROEMainBar', chunk);

      if (error) throw error;

      if (chunkResults && chunkResults.length > 0) {
        allResults = allResults.concat(chunkResults);
      }
    }

    // Create a map for quick lookup (use first match per panel serial)
    const panelToOrderMap = {};
    allResults.forEach(row => {
      if (!panelToOrderMap[row.ROEMainBar]) {
        panelToOrderMap[row.ROEMainBar] = row.dodno;
      }
    });

    // Create response with original order and results
    const results = panelSerials.map(serial => ({
      panel_serial: serial,
      order_number: serial && typeof serial === 'string' && serial.trim() ?
        (panelToOrderMap[serial.trim()] || null) : null,
      found: serial && typeof serial === 'string' && serial.trim() ?
        !!panelToOrderMap[serial.trim()] : false
    }));

    // Count unique found serials (not total rows, since one panel maps to many modules)
    const foundCount = results.filter(r => r.found).length;

    return {
      summary: {
        total_requested: panelSerials.length,
        valid_serials: validSerials.length,
        found_matches: foundCount,
        not_found: validSerials.length - foundCount
      },
      results: results
    };
  } catch (error) {
    console.error('Batch panel search error:', error);
    throw error;
  }
}

// Batch module serial to order number lookup
export async function batchModuleSearch(moduleSerials) {
  try {
    const validSerials = moduleSerials
      .filter(serial => serial && typeof serial === 'string' && serial.trim().length > 0)
      .map(serial => serial.trim());

    if (validSerials.length === 0) {
      return {
        summary: {
          total_requested: moduleSerials.length,
          valid_serials: 0,
          found_matches: 0,
          not_found: 0
        },
        results: []
      };
    }

    // Query in chunks to avoid hitting Supabase limits
    const chunkSize = 100;
    let allResults = [];

    for (let i = 0; i < validSerials.length; i += chunkSize) {
      const chunk = validSerials.slice(i, i + chunkSize);

      const { data: chunkResults, error } = await supabase
        .from('ROEList')
        .select('ROESubBar, dodno')
        .in('ROESubBar', chunk);

      if (error) throw error;

      if (chunkResults && chunkResults.length > 0) {
        allResults = allResults.concat(chunkResults);
      }
    }

    // Create a map for quick lookup
    const moduleToOrderMap = {};
    allResults.forEach(row => {
      moduleToOrderMap[row.ROESubBar] = row.dodno;
    });

    // Create response with original order and results
    const results = moduleSerials.map(serial => ({
      module_serial: serial,
      order_number: serial && typeof serial === 'string' && serial.trim() ?
        (moduleToOrderMap[serial.trim()] || null) : null,
      found: serial && typeof serial === 'string' && serial.trim() ?
        !!moduleToOrderMap[serial.trim()] : false
    }));

    return {
      summary: {
        total_requested: moduleSerials.length,
        valid_serials: validSerials.length,
        found_matches: allResults.length,
        not_found: validSerials.length - allResults.length
      },
      results: results
    };
  } catch (error) {
    console.error('Batch module search error:', error);
    throw error;
  }
}
